// Renders a NavList. Used by both the UtilityPage sidebar (page.sidebar_nav)
// and the footer (FooterSettings.nav_lists): a heading accordion plus
// per-block-type rendering, where each block type (link / offers_link /
// retail_locator_link / reviews_link / html) renders differently -- unlike the
// flat <a> rendering of the header's NavList.
//
// NOTE: the underlying snippet receives a `current_page` variable but never
// uses it (no current-page highlighting actually happens), and the body is a
// flat list of links under a single accordion heading -- there is no nesting.
import React, { useEffect, useId, useState } from "react";
import { getReviewAPIURL } from "tsi-common-react/src/api/reviews";
import { fetchJson } from "tsi-common-react/src/utils/ajax";

import { NavList_16E9563B6D } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { RetailLocatorStoreCount } from "../../apps/retail-locator/RetailLocatorStoreCount";

import styles from "./index.module.scss";

type INavList = NavList_16E9563B6D;
type INavListItem = INavList["body"]["value"][number];
type IReviewsLinkValue = Extract<
    INavListItem,
    { type: "reviews_link" }
>["value"];

// The stearns reviews brand id, passed to the external reviews API.
const REVIEWS_BRAND_ID = "2";

// The header/footer reviews link, with the total review count fetched client-
// side and appended as an <em> (styled "(N)" by a.reviewsLink em in
// index.module.scss). The fetch is client-only (useEffect), so the
// server render and the first client render carry no <em> -- the count appears
// after the request resolves, with no hydration mismatch.
const ReviewsLink = ({
    value,
}: {
    value: IReviewsLinkValue;
}): React.JSX.Element => {
    const [count, setCount] = useState<number | null>(null);
    useEffect(() => {
        let cancelled = false;
        const loadCount = async () => {
            const apiURL = getReviewAPIURL("/reviews");
            const query = new URLSearchParams({ brand_id: REVIEWS_BRAND_ID });
            const resp = await fetchJson<{ count: number }>(
                `${apiURL}?${query}`,
            );
            if (!cancelled) {
                setCount(resp.count);
            }
        };
        loadCount().catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);
    return (
        <a
            // "reviews-link" is targeted by general/_header.scss
            className={concatClassNames(["reviews-link", styles.reviewsLink])}
            href={value.page?.url ?? ""}
        >
            {value.display_name}
            {count !== null && <em>{count}</em>}
        </a>
    );
};

const NavLink = ({
    item,
    offersCount,
}: {
    item: INavListItem;
    offersCount: number;
}): React.JSX.Element | null => {
    switch (item.type) {
        case "link": {
            // cms/blocks/link-block.html
            const href = item.value.page
                ? item.value.page.url
                : item.value.external_url || "#";
            return (
                // class omitted (not class="") when not a button.
                <a
                    href={href}
                    className={item.value.is_button ? "button" : undefined}
                >
                    {item.value.display_name}
                </a>
            );
        }
        case "offers_link": {
            return (
                // href="" (not omitted) when page is unset.
                <a
                    className={concatClassNames([
                        // targeted by general/_header.scss
                        "offers-link",
                        styles.offersLink,
                    ])}
                    href={item.value.page?.url ?? ""}
                >
                    {item.value.display_name}
                    <em>{offersCount}</em>
                </a>
            );
        }
        case "retail_locator_link": {
            return (
                <a
                    className={concatClassNames([
                        // targeted by general/_header.scss
                        "retail-locator-link",
                        styles.retailLocatorLink,
                    ])}
                    href={item.value.page?.url ?? ""}
                >
                    {item.value.display_name}
                    <em>
                        <RetailLocatorStoreCount type="bare-count" />
                    </em>
                </a>
            );
        }
        case "reviews_link": {
            return <ReviewsLink value={item.value} />;
        }
        case "html": {
            // RawHTMLBlock: opaque CMS HTML injected verbatim. React can't
            // meaningfully reconcile dangerouslySetInnerHTML during hydration,
            // and this content is routinely rewritten between the server string
            // and the parsed/live DOM by forces React doesn't model: the HTML
            // parser normalizes CR/CRLF to LF, and third-party scripts wire it
            // up (OneTrust's otSDKStub trims the "Cookie Settings" label inside
            // #ot-sdk-btn after parse -- verified: the parser keeps the leading
            // space, OneTrust strips it). Both yield benign "won't be patched
            // up" attribute mismatches, so suppress hydration warnings on this
            // opaque block (a genuine third-party-rewrite Class-C case, like the
            // Wistia rich-text container).
            return (
                <EditorContent>
                    {(marker) => (
                        <div
                            suppressHydrationWarning
                            dangerouslySetInnerHTML={{ __html: item.value }}
                            {...marker}
                        />
                    )}
                </EditorContent>
            );
        }
        default:
            return null;
    }
};

export const NavListSnippet = ({
    navList,
    offersCount,
}: {
    navList: INavList;
    offersCount: number;
}): React.JSX.Element => {
    // Accordion toggle owned by this component's own state. Defaults open to
    // match the static markup (`--is-active` / `aria-expanded="true"` /
    // `aria-hidden="false"`).
    //
    // Open state is driven by the `accordion-target--is-active` modifier on the
    // target, NOT by aria-hidden: the closed default (_accordion.scss) is
    // max-height:0 / visibility:hidden, and only `.accordion-target--is-active`
    // (footer) / the sidebar override opens it — so React must render the
    // modifier itself or the columns stay collapsed.
    const [isOpen, setIsOpen] = useState(true);
    const targetId = useId();
    return (
        <nav className="nav-list">
            {!navList.hide_heading && (
                <h5>
                    <button
                        className={`accordion-trigger${
                            isOpen ? " --is-active" : ""
                        }`}
                        aria-controls={targetId}
                        aria-expanded={isOpen ? "true" : "false"}
                        onClick={(event) => {
                            event.preventDefault();
                            setIsOpen((value) => !value);
                        }}
                    >
                        {navList.heading}
                    </button>
                </h5>
            )}
            <div
                className={`accordion-target${
                    isOpen ? " accordion-target--is-active" : ""
                }`}
                id={targetId}
                aria-hidden={isOpen ? "false" : "true"}
            >
                {navList.body.value.map((item) => (
                    <NavLink
                        key={item.id}
                        item={item}
                        offersCount={offersCount}
                    />
                ))}
            </div>
        </nav>
    );
};
